import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useStartListGroups } from '../../../api/hooks/useStartListGroups';
import { useStartList } from '../../../api/hooks/useStartList';
import { StartListGenerateButton } from '../../../components/startlist/StartListGenerateButton';
import { StartListHeatsView } from '../../../components/startlist/StartListHeatsView';
import { START_LIST_TARGET_LABELS } from '../../../types/startList';
import type { StartListGroup } from '../../../types/startList';

interface Props {
  disciplineId: number;
  disciplineName?: string;
}

/** Vygenerovana startovka per skupina v ramci jedne discipliny. */
export function StartListTab({ disciplineId, disciplineName }: Props) {
  const { data: groups = [], isLoading } = useStartListGroups(disciplineId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        Tato disciplina nema zadne startovaci skupiny. Zalozte je v zalozce „Konfigurace startovky".
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">
          Startovky{disciplineName ? ` — ${disciplineName}` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pro kazdou skupinu vygenerujte rozbehy a v pripade kolize doladte rucne.
        </Typography>
      </Box>
      {groups.map((g) => (
        <GroupSection key={g.id} group={g} />
      ))}
    </Stack>
  );
}

function GroupSection({ group }: { group: StartListGroup }) {
  const { data: startList } = useStartList(group.id);
  const heatsCount = startList?.heats.length ?? 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {group.name}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label={START_LIST_TARGET_LABELS[group.target]}
                color={group.target === 'TEAM' ? 'primary' : 'secondary'}
              />
              <Chip size="small" label={`${group.parallelLanes} drahy`} variant="outlined" />
              {heatsCount > 0 && (
                <Chip size="small" label={`${heatsCount} rozbehu`} variant="outlined" />
              )}
            </Stack>
          </Box>
          <StartListGenerateButton
            groupId={group.id}
            hasExistingStartList={heatsCount > 0}
          />
        </Stack>

        {startList && startList.heats.length > 0 ? (
          <StartListHeatsView startList={startList} editable />
        ) : (
          <Alert severity="info" sx={{ mt: 1 }}>
            Startovka jeste nebyla vygenerovana.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
